// Rule-based (never an LLM call) classifier for Inbox & Triage — ICT-4 routing.
// Pure, dependency-free.
import type { ProxyInboxEmail } from '../data/mockProxyEmails';

export type ProxyEmailCategory =
  | 'MATERIAL_PDF' | 'JOB_CHANGE' | 'BILLING_VERIFICATION'
  | 'REVISED_RDQ' | 'PROXY_CONTEST' | 'CREDIT_HOLD' | 'RED_REPORT' | 'MATERIAL_SHORTAGE'
  | 'FOLLOW_UP_REPORT' | 'BOUNCE' | 'FOLLOW_UP_REPLY' | 'REPORT';

export const EMAIL_CATEGORY_LABEL: Record<ProxyEmailCategory, string> = {
  MATERIAL_PDF: 'Material PDF',
  JOB_CHANGE: 'Job Change',
  BILLING_VERIFICATION: 'Billing Verification',
  REVISED_RDQ: 'Revised RDQ',
  PROXY_CONTEST: 'Proxy Contest',
  CREDIT_HOLD: 'Credit Hold',
  RED_REPORT: 'Red Report',
  MATERIAL_SHORTAGE: 'Material Shortage',
  FOLLOW_UP_REPORT: 'Follow-Up Report',
  BOUNCE: 'Bounced Email',
  FOLLOW_UP_REPLY: 'Solicitor Reply',
  REPORT: 'Report',
};

export interface ClassificationResult {
  category: ProxyEmailCategory | null;
  routeTo: string;
  confidence: number;
  why: string;
  fileIn?: string;   // ICT-4 Outlook folder the email is filed in once actioned
}

interface Rule {
  category: ProxyEmailCategory;
  routeTo: string;
  test: (text: string) => boolean;
  why: string;
  fileIn?: string;
}

const RULES: Rule[] = [
  {
    // Checked first — a bounce notice quotes the original message back
    // (subject/body), which would otherwise false-match whatever category
    // that original mail was.
    category: 'BOUNCE', routeTo: 'Proxy Exceptions (contact needs verification)',
    test: t => /delivery status notification|mail delivery (system|subsystem)|undeliverable|mailer-daemon/.test(t),
    why: 'Mail sent to the contact on file bounced back undeliverable.',
  },
  {
    // Ahead of JOB_CHANGE/MATERIAL_PDF/MATERIAL_SHORTAGE: a reply to one of
    // our chases (material or shortage) can mention a date change, attach
    // PDFs or talk about the shortage, and the reply workflow handles all of it.
    category: 'FOLLOW_UP_REPLY', routeTo: 'FOLLOW_UP_REPLY workflow (ICT-1, ICT-8 and ICT-13 responses)',
    test: t => /^re: .*(follow-up|material request|shortage)/.test(t),
    why: 'A reply to one of our material follow-ups.',
  },
  {
    category: 'FOLLOW_UP_REPORT', routeTo: 'MATERIAL_FOLLOWUP workflow (every row, one email per solicitor)',
    test: t => /ict follow ups report/.test(t),
    why: 'The daily ICT Follow-Up Report (ICT-1/ICT-8), listing jobs still owed material.',
  },
  {
    category: 'RED_REPORT', fileIn: 'Red Reports', routeTo: 'Red Reports repository (file, no action)',
    test: t => /red report/.test(t),
    why: 'Subject mentions "Red Report".',
  },
  {
    // ICT-4 "Reports": scheduled reports from Report Manager. Deleted when
    // the ICT manager is in; forwarded to the team, then deleted, when not.
    category: 'REPORT', routeTo: 'Delete, or forward to the ICT team first if the manager is out',
    test: t => /^report: |your scheduled report/.test(t),
    why: 'A scheduled report from Report Manager.',
  },
  {
    category: 'PROXY_CONTEST', fileIn: 'Proxy Contest Forms', routeTo: 'ICT contest associate (ict-contest@betanxt.com)',
    test: t => /contest|dissident/.test(t),
    why: 'Mentions a proxy contest / dissident party.',
  },
  {
    category: 'CREDIT_HOLD', fileIn: 'No Fulfillment', routeTo: 'ICT credit associate (ict-credit@betanxt.com)',
    test: t => /credit hold|no fulfillment/.test(t),
    why: 'Mentions a credit hold.',
  },
  {
    category: 'MATERIAL_SHORTAGE', routeTo: 'ICT material associate (proxymaterial@betanxt.com)',
    test: t => /shortage|received short/.test(t),
    why: 'Fulfillment reported a material shortage.',
  },
  {
    category: 'REVISED_RDQ', fileIn: 'ICT Requests Sent', routeTo: 'ICT RDQ associate (ict-rdq@betanxt.com)',
    test: t => /rdq/.test(t),
    why: 'Mentions an RDQ (reorder quantity) request.',
  },
  {
    category: 'BILLING_VERIFICATION', fileIn: 'ICT Requests Sent', routeTo: 'ICT billing associate (ict-billing@betanxt.com)',
    test: t => /billing/.test(t),
    why: 'Mentions billing verification.',
  },
  {
    category: 'JOB_CHANGE', fileIn: 'Miscellaneous Completed', routeTo: 'documents@, verifications@, notifications@betanxt.com',
    test: t => /distribution method|job date|meeting date|rescheduled/.test(t),
    why: 'Mentions a job date or distribution-method change.',
  },
  {
    category: 'MATERIAL_PDF', fileIn: 'Miscellaneous Completed', routeTo: 'documents@, verifications@, notifications@betanxt.com',
    test: t => /pdf|proxy statement|notice\/circular|form of proxy|attached:/.test(t),
    why: 'Attaches proxy material.',
  },
];

export function classifyEmail(email: ProxyInboxEmail): ClassificationResult {
  const text = `${email.subject} ${email.body}`.toLowerCase();
  for (const rule of RULES) {
    if (rule.test(text)) {
      return { category: rule.category, routeTo: rule.routeTo, confidence: 0.9, why: rule.why, fileIn: rule.fileIn };
    }
  }
  return { category: null, routeTo: 'Manual review', confidence: 0, why: 'No rule matched, so this needs manual triage.' };
}
